import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setForm(blank);
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    load();
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      address: c.address || "",
      notes: c.notes || "",
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(blank);
  };

  return (
    <div className="stagger-in">
      <div className="label-mono">/ CLIENTS</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">Clients</h1>
      <p className="text-zinc-400 mt-2">Your customer list.</p>

      <div className="grid lg:grid-cols-3 gap-0 mt-10 border border-[var(--border)]">
        <div className="lg:col-span-1 p-8 border-r border-[var(--border)]">
          <div className="label-mono mb-4">{editing ? "Edit client" : "New client"}</div>
          <div className="space-y-3">
            <Input label="Name" value={form.name} onChange={set("name")} testid="client-name" />
            <Input label="Email" value={form.email} onChange={set("email")} testid="client-email" />
            <Input label="Phone" value={form.phone} onChange={set("phone")} testid="client-phone" />
            <Input
              label="Address"
              value={form.address}
              onChange={set("address")}
              testid="client-address"
            />
            <Input
              label="Notes"
              value={form.notes}
              onChange={set("notes")}
              testid="client-notes"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary"
              data-testid="client-save-btn"
            >
              {editing ? <Save size={14} /> : <Plus size={14} />}{" "}
              {editing ? "Update" : "Add"}
            </button>
            {editing && (
              <button onClick={cancel} className="btn-ghost" data-testid="client-cancel-btn">
                <X size={14} /> Cancel
              </button>
            )}
          </div>
        </div>
        <div className="lg:col-span-2 p-8">
          <div className="label-mono mb-4">All clients ({clients.length})</div>
          <div className="border border-[var(--border)] overflow-x-auto" data-testid="clients-list">
            <div className="min-w-[720px]">
            <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
              <div className="col-span-3 p-3">Name</div>
              <div className="col-span-3 p-3 border-l border-[var(--border)]">Email</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">Phone</div>
              <div className="col-span-3 p-3 border-l border-[var(--border)]">Address</div>
              <div className="col-span-1 p-3 border-l border-[var(--border)]" />
            </div>
            {clients.length === 0 && (
              <div className="p-8 text-center text-zinc-500 font-mono text-sm">
                No clients yet. Add your first one on the left.
              </div>
            )}
            {clients.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
                data-testid={`client-row-${c.id}`}
              >
                <div className="col-span-3 p-3 truncate">{c.name}</div>
                <div className="col-span-3 p-3 border-l border-[var(--border)] truncate text-zinc-400">
                  {c.email || "—"}
                </div>
                <div className="col-span-2 p-3 border-l border-[var(--border)] truncate text-zinc-400">
                  {c.phone || "—"}
                </div>
                <div className="col-span-3 p-3 border-l border-[var(--border)] truncate text-zinc-400">
                  {c.address || "—"}
                </div>
                <div className="col-span-1 border-l border-[var(--border)] flex">
                  <button
                    onClick={() => startEdit(c)}
                    className="flex-1 border-r border-[var(--border)] text-zinc-500 hover:text-[var(--primary)] flex items-center justify-center"
                    data-testid={`client-edit-${c.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    className="flex-1 text-zinc-500 hover:text-[var(--error)] flex items-center justify-center"
                    data-testid={`client-delete-${c.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <input
        className="input-sharp"
        value={value}
        onChange={onChange}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
